import type { UpdateResolver, UpdatesConfig, Variables } from '@urql/exchange-graphcache';
import { GetOrderDocument } from '../generated/shopping/GetOrderDocument';
import type {
  AfterPaymentMutation,
  GetOrderQuery,
  GetOrderQueryVariables,
  PlaceOrderMutation,
} from '../generated/shopping/graphql';

/**
 * Cache semantics of a placed order.
 *
 * `placeOrder` and `afterPayment` both answer a successful order with the same
 * `CheckoutOrderConfirmation` that `getOrder(locale, orderId)` returns, and both end in a
 * client-side navigation to the confirmation page, which then queried the very order the
 * mutation had just delivered. Writing the mutation's order into that query's cache entry
 * removes the second round trip.
 *
 * The entry is seeded: the confirmation page has not been visited when the mutation runs, so
 * there is nothing to update yet. Nothing else is touched — an order is immutable, so no
 * other cached order can be affected, and a failure result leaves the cache alone.
 *
 * The write only pays off while the mutations select at least everything the query selects,
 * which is why all three share `CheckoutOrderConfirmationFragment`. `PlacedOrderResult` is
 * derived from the mutations' generated types, so dropping that fragment from one of them is a
 * compile error here rather than a silent fall back to the network.
 *
 * The cache lives in the client held by the `/kasse` layout. A reload or a link shared into a
 * fresh tab has no such client and reads the order from the backend.
 */

const ORDER_FIELD = 'getOrder';

/** The success result both order-placing mutations share. */
type PlacedOrderResult = Extract<
  PlaceOrderMutation['placeOrder'] | AfterPaymentMutation['afterPayment'],
  { __typename: 'CheckoutPlaceOrderSuccess' }
>;

const isPlacedOrder = (value: unknown): value is PlacedOrderResult =>
  typeof value === 'object' &&
  value !== null &&
  '__typename' in value &&
  value.__typename === 'CheckoutPlaceOrderSuccess' &&
  'orderId' in value &&
  typeof value.orderId === 'string' &&
  // A response without the order must not write a hollow entry.
  'order' in value &&
  value.order !== null;

const localeArgument = (args: Variables | null | undefined): string | undefined => {
  const locale = args?.locale;
  return typeof locale === 'string' ? locale : undefined;
};

const seedOrderConfirmation =
  (field: string): UpdateResolver =>
  (result, args, cache) => {
    const placed = result[field];
    if (!isPlacedOrder(placed)) {
      return;
    }

    const locale = localeArgument(args);
    if (locale === undefined) {
      return;
    }

    cache.updateQuery<GetOrderQuery, GetOrderQueryVariables>(
      { query: GetOrderDocument, variables: { locale, orderId: placed.orderId } },
      () => ({
        [ORDER_FIELD]: { __typename: 'CheckoutGetOrderSuccess', order: placed.order },
      }),
    );
  };

export const orderConfirmationCacheUpdates: UpdatesConfig['Mutation'] = {
  placeOrder: seedOrderConfirmation('placeOrder'),
  afterPayment: seedOrderConfirmation('afterPayment'),
};
